import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  TrendingDown, 
  Calendar, 
  DollarSign, 
  Settings, 
  Play, 
  Pause, 
  History,
  AlertCircle,
  Check,
  Car,
  Clock,
  Percent,
  ChevronRight,
  Save,
  RotateCcw,
  Search,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  ExternalLink,
  Eye,
  Target
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: '0', label: 'Dimanche' },
  { value: '1', label: 'Lundi' },
  { value: '2', label: 'Mardi' },
  { value: '3', label: 'Mercredi' },
  { value: '4', label: 'Jeudi' },
  { value: '5', label: 'Vendredi' },
  { value: '6', label: 'Samedi' },
];

const AdminPricing = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [activeTab, setActiveTab] = useState('pricing');
  
  // Competitor monitoring state
  const [competitorData, setCompetitorData] = useState([]);
  const [scanningPrices, setScanningPrices] = useState(false);
  const [lastScanDate, setLastScanDate] = useState(null);
  
  // Cron job status
  const [cronStatus, setCronStatus] = useState({
    enabled: false,
    scheduler_running: false,
    next_scheduled_run: null,
    last_automatic_run: null,
    last_vehicles_updated: 0
  });
  
  // Pricing rule settings
  const [settings, setSettings] = useState({
    enabled: false,
    dropDay: '1', // Monday by default
    dropType: 'fixed', // 'fixed' or 'percentage'
    dropAmount: 500,
    dropPercentage: 2,
    minPrice: 5000,
    applyToAll: true,
    excludeSold: true,
    excludeReserved: true,
    maxDropsPerVehicle: 12, // Max number of drops before stopping
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, settingsRes, historyRes, competitorRes, cronRes] = await Promise.all([
        axios.get(`${API}/vehicles?limit=200&include_offline=true`, { headers: getAuthHeader() }),
        axios.get(`${API}/pricing/settings`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        axios.get(`${API}/pricing/history?limit=50`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/pricing/competitor-data`, { headers: getAuthHeader() }).catch(() => ({ data: { data: [], last_scan: null } })),
        axios.get(`${API}/pricing/cron-status`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
      ]);
      
      setVehicles(vehiclesRes.data);
      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
        if (settingsRes.data.selectedVehicleIds) {
          setSelectedVehicles(settingsRes.data.selectedVehicleIds);
        }
      }
      setPriceHistory(historyRes.data || []);
      if (competitorRes.data) {
        setCompetitorData(competitorRes.data.data || []);
        setLastScanDate(competitorRes.data.last_scan);
      }
      if (cronRes.data) {
        setCronStatus(cronRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pricing/settings`, {
        ...settings,
        selectedVehicleIds: settings.applyToAll ? [] : selectedVehicles,
      }, {
        headers: getAuthHeader(),
      });
      toast.success('Paramètres sauvegardés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyNow = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir appliquer la réduction maintenant?')) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/pricing/apply-now`, {
        ...settings,
        selectedVehicleIds: settings.applyToAll ? [] : selectedVehicles,
      }, {
        headers: getAuthHeader(),
      });
      toast.success(`Prix mis à jour pour ${response.data.updated_count} véhicule(s)`);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error("Erreur lors de l'application");
    } finally {
      setSaving(false);
    }
  };

  // Scan competitor prices
  const handleScanPrices = async () => {
    setScanningPrices(true);
    toast.info('Analyse des prix en cours... Cela peut prendre quelques minutes.');
    
    try {
      const response = await axios.post(`${API}/pricing/scan-competitors`, {}, {
        headers: getAuthHeader(),
        timeout: 120000, // 2 minute timeout
      });
      
      setCompetitorData(response.data.data || []);
      setLastScanDate(response.data.last_scan);
      toast.success(`Analyse terminée: ${response.data.vehicles_analyzed} véhicules analysés`);
    } catch (error) {
      toast.error("Erreur lors de l'analyse des prix");
    } finally {
      setScanningPrices(false);
    }
  };

  // Get competitiveness stats
  const getCompetitorStats = () => {
    if (competitorData.length === 0) return { competitive: 0, overpriced: 0, underpriced: 0, noData: 0 };
    
    return competitorData.reduce((acc, item) => {
      if (!item.market_avg || item.market_avg === 0) {
        acc.noData++;
      } else if (item.your_price <= item.market_avg * 0.95) {
        acc.competitive++;
      } else if (item.your_price > item.market_avg * 1.05) {
        acc.overpriced++;
      } else {
        acc.competitive++;
      }
      return acc;
    }, { competitive: 0, overpriced: 0, underpriced: 0, noData: 0 });
  };

  const toggleVehicleSelection = (vehicleId) => {
    setSelectedVehicles(prev => 
      prev.includes(vehicleId) 
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const eligibleIds = getEligibleVehicles().map(v => v.id);
      setSelectedVehicles(eligibleIds);
    } else {
      setSelectedVehicles([]);
    }
  };

  const getEligibleVehicles = () => {
    return vehicles.filter(v => {
      if (settings.excludeSold && v.status === 'sold') return false;
      if (settings.excludeReserved && v.status === 'reserved') return false;
      if (v.status === 'offline') return false;
      if (v.price <= settings.minPrice) return false;
      return true;
    });
  };

  const calculateNewPrice = (currentPrice) => {
    if (settings.dropType === 'fixed') {
      return Math.max(currentPrice - settings.dropAmount, settings.minPrice);
    } else {
      const reduction = currentPrice * (settings.dropPercentage / 100);
      return Math.max(currentPrice - reduction, settings.minPrice);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const eligibleVehicles = getEligibleVehicles();
  const vehiclesToShow = settings.applyToAll ? eligibleVehicles : eligibleVehicles.filter(v => selectedVehicles.includes(v.id));

  // Calculate potential savings
  const totalCurrentValue = vehiclesToShow.reduce((sum, v) => sum + v.price, 0);
  const totalNewValue = vehiclesToShow.reduce((sum, v) => sum + calculateNewPrice(v.price), 0);
  const totalReduction = totalCurrentValue - totalNewValue;
  
  // Competitor stats
  const competitorStats = getCompetitorStats();

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-pricing">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <TrendingDown className="h-6 w-6 text-blue-600" />
              Outil de tarification
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Gérez vos prix et surveillez la concurrence
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="pricing" className="data-[state=active]:bg-white">
              <TrendingDown className="h-4 w-4 mr-2" />
              Réduction automatique
            </TabsTrigger>
            <TabsTrigger value="competitor" className="data-[state=active]:bg-white">
              <Target className="h-4 w-4 mr-2" />
              Surveillance concurrence
              {competitorStats.overpriced > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-[10px]">{competitorStats.overpriced}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Pricing Tab Content */}
          <TabsContent value="pricing" className="space-y-4">
            {/* Action Buttons */}
            <div className="flex items-center gap-3 justify-end">
              <Button 
                variant="outline" 
                onClick={handleApplyNow}
                disabled={saving || vehiclesToShow.length === 0}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <Play className="h-4 w-4 mr-2" />
                Appliquer maintenant
              </Button>
              <Button 
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="lg:col-span-1 space-y-4">
            {/* Enable/Disable Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Activation
                  </CardTitle>
                  <Switch
                    checked={settings.enabled}
                    onCheckedChange={async (checked) => {
                      setSettings(prev => ({ ...prev, enabled: checked }));
                      try {
                        await axios.post(`${API}/pricing/settings`, {
                          ...settings,
                          enabled: checked,
                          selectedVehicleIds: settings.applyToAll ? [] : selectedVehicles,
                        }, { headers: getAuthHeader() });
                        toast.success(checked ? 'Réduction automatique activée' : 'Réduction automatique désactivée');
                      } catch (error) {
                        toast.error('Erreur lors de la sauvegarde');
                        setSettings(prev => ({ ...prev, enabled: !checked }));
                      }
                    }}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-500">
                  {settings.enabled ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <Check className="h-4 w-4" /> Réduction automatique activée
                    </span>
                  ) : (
                    <span className="text-slate-400 flex items-center gap-1">
                      <Pause className="h-4 w-4" /> Réduction automatique désactivée
                    </span>
                  )}
                </p>
                
                {/* Cron Status */}
                {cronStatus.scheduler_running && (
                  <div className="pt-2 border-t border-slate-100 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock className="h-3 w-3" />
                      <span>Planificateur: </span>
                      <Badge className="bg-green-100 text-green-700 text-[10px]">Actif</Badge>
                    </div>
                    {cronStatus.next_scheduled_run && (
                      <div className="text-xs text-slate-500">
                        <span>Prochaine exécution: </span>
                        <span className="font-medium text-slate-700">
                          {new Date(cronStatus.next_scheduled_run).toLocaleDateString('fr-CA', { 
                            weekday: 'long', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>
                    )}
                    {cronStatus.last_automatic_run && (
                      <div className="text-xs text-slate-500">
                        <span>Dernière exécution auto: </span>
                        <span className="font-medium text-slate-700">
                          {new Date(cronStatus.last_automatic_run).toLocaleDateString('fr-CA', { 
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                          {cronStatus.last_vehicles_updated > 0 && (
                            <span className="text-green-600 ml-1">
                              ({cronStatus.last_vehicles_updated} véhicules)
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Schedule Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Planification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-slate-600">Jour de la semaine</Label>
                  <Select 
                    value={settings.dropDay} 
                    onValueChange={(value) => setSettings(prev => ({ ...prev, dropDay: value }))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map(day => (
                        <SelectItem key={day.value} value={day.value}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                  <Clock className="h-3 w-3 inline mr-1" />
                  La réduction sera appliquée chaque <strong>{DAYS_OF_WEEK.find(d => d.value === settings.dropDay)?.label}</strong> à minuit.
                </div>
              </CardContent>
            </Card>

            {/* Reduction Amount Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Montant de réduction
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-slate-600">Type de réduction</Label>
                  <Select 
                    value={settings.dropType} 
                    onValueChange={(value) => setSettings(prev => ({ ...prev, dropType: value }))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Montant fixe ($)</SelectItem>
                      <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {settings.dropType === 'fixed' ? (
                  <div>
                    <Label className="text-sm text-slate-600">Montant ($)</Label>
                    <div className="relative mt-1.5">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="number"
                        value={settings.dropAmount}
                        onChange={(e) => setSettings(prev => ({ ...prev, dropAmount: parseInt(e.target.value) || 0 }))}
                        className="pl-9"
                        min="0"
                        step="100"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm text-slate-600">Pourcentage (%)</Label>
                    <div className="relative mt-1.5">
                      <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="number"
                        value={settings.dropPercentage}
                        onChange={(e) => setSettings(prev => ({ ...prev, dropPercentage: parseFloat(e.target.value) || 0 }))}
                        className="pl-9"
                        min="0"
                        max="50"
                        step="0.5"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-sm text-slate-600">Prix minimum</Label>
                  <div className="relative mt-1.5">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="number"
                      value={settings.minPrice}
                      onChange={(e) => setSettings(prev => ({ ...prev, minPrice: parseInt(e.target.value) || 0 }))}
                      className="pl-9"
                      min="0"
                      step="500"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Le prix ne descendra jamais en dessous de ce montant
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Filters Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  Véhicules ciblés
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-slate-600">Appliquer à tous les véhicules</Label>
                  <Switch
                    checked={settings.applyToAll}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, applyToAll: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-slate-600">Exclure les vendus</Label>
                  <Switch
                    checked={settings.excludeSold}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, excludeSold: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-slate-600">Exclure les réservés</Label>
                  <Switch
                    checked={settings.excludeReserved}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, excludeReserved: checked }))}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-2 space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-blue-700">{vehiclesToShow.length}</p>
                  <p className="text-xs text-blue-600">Véhicules ciblés</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-amber-700">{formatPrice(totalReduction)}</p>
                  <p className="text-xs text-amber-600">Réduction totale</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <p className="text-lg font-bold text-green-700">
                    {settings.dropType === 'fixed' 
                      ? formatPrice(settings.dropAmount)
                      : `${settings.dropPercentage}%`
                    }
                  </p>
                  <p className="text-xs text-green-600">Par véhicule</p>
                </CardContent>
              </Card>
            </div>

            {/* Vehicle Preview Table */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    Aperçu des changements
                  </CardTitle>
                  {!settings.applyToAll && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectAll}
                        onCheckedChange={handleSelectAll}
                      />
                      <span className="text-sm text-slate-500">Tout sélectionner</span>
                    </div>
                  )}
                </div>
                <CardDescription>
                  {settings.applyToAll 
                    ? 'Tous les véhicules éligibles seront affectés'
                    : `${selectedVehicles.length} véhicule(s) sélectionné(s)`
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        {!settings.applyToAll && (
                          <TableHead className="w-10"></TableHead>
                        )}
                        <TableHead className="text-xs">Véhicule</TableHead>
                        <TableHead className="text-xs text-right">Prix actuel</TableHead>
                        <TableHead className="text-xs text-center">
                          <ChevronRight className="h-4 w-4 mx-auto" />
                        </TableHead>
                        <TableHead className="text-xs text-right">Nouveau prix</TableHead>
                        <TableHead className="text-xs text-right">Réduction</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eligibleVehicles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={settings.applyToAll ? 5 : 6} className="text-center text-slate-500 py-8">
                            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                            Aucun véhicule éligible
                          </TableCell>
                        </TableRow>
                      ) : (
                        eligibleVehicles.map((vehicle) => {
                          const newPrice = calculateNewPrice(vehicle.price);
                          const reduction = vehicle.price - newPrice;
                          const isSelected = settings.applyToAll || selectedVehicles.includes(vehicle.id);
                          
                          return (
                            <TableRow 
                              key={vehicle.id} 
                              className={`${isSelected ? 'bg-blue-50/50' : 'opacity-50'}`}
                            >
                              {!settings.applyToAll && (
                                <TableCell>
                                  <Checkbox
                                    checked={selectedVehicles.includes(vehicle.id)}
                                    onCheckedChange={() => toggleVehicleSelection(vehicle.id)}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="w-10 h-8 rounded bg-slate-100 overflow-hidden">
                                    {vehicle.images?.[0] ? (
                                      <img src={vehicle.images[0]} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <Car className="w-full h-full p-1.5 text-slate-300" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-800">
                                      {vehicle.year} {vehicle.make} {vehicle.model}
                                    </p>
                                    <p className="text-xs text-slate-500">{vehicle.stock_number || '—'}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium text-slate-700">
                                {formatPrice(vehicle.price)}
                              </TableCell>
                              <TableCell className="text-center">
                                <TrendingDown className="h-4 w-4 text-red-500 mx-auto" />
                              </TableCell>
                              <TableCell className="text-right font-bold text-green-600">
                                {formatPrice(newPrice)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                                  -{formatPrice(reduction)}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Price History */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Historique des réductions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[200px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Véhicules</TableHead>
                        <TableHead className="text-xs text-right">Réduction totale</TableHead>
                        <TableHead className="text-xs text-center">Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {priceHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-500 py-6">
                            Aucun historique disponible
                          </TableCell>
                        </TableRow>
                      ) : (
                        priceHistory.map((entry, index) => (
                          <TableRow key={index}>
                            <TableCell className="text-sm text-slate-600">
                              {formatDate(entry.date)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {entry.vehicle_count} véhicule(s)
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-red-600">
                              -{formatPrice(entry.total_reduction)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="text-xs">
                                {entry.type === 'automatic' ? 'Auto' : 'Manuel'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
          </TabsContent>

          {/* Competitor Monitoring Tab */}
          <TabsContent value="competitor" className="space-y-4">
            {/* Competitor Header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">
                  {lastScanDate 
                    ? `Dernière analyse: ${formatDate(lastScanDate)}`
                    : 'Aucune analyse effectuée'
                  }
                </p>
              </div>
              <Button 
                onClick={handleScanPrices}
                disabled={scanningPrices}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {scanningPrices ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Analyser les prix AutoTrader
                  </>
                )}
              </Button>
            </div>

            {/* Competitor Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700">{competitorStats.competitive}</p>
                      <p className="text-xs text-green-600">Prix compétitif</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-700">{competitorStats.overpriced}</p>
                      <p className="text-xs text-red-600">Prix trop élevé</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <TrendingDown className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-700">{competitorStats.underpriced}</p>
                      <p className="text-xs text-blue-600">Sous-évalué</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Eye className="h-5 w-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-700">{competitorStats.noData}</p>
                      <p className="text-xs text-slate-500">Sans données</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Competitor Data Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Comparaison des prix vs AutoTrader
                </CardTitle>
                <CardDescription>
                  Véhicules triés par écart de prix (plus élevé en premier)
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Votre véhicule</TableHead>
                        <TableHead className="text-xs text-right">Votre prix</TableHead>
                        <TableHead className="text-xs text-right">Prix moyen marché</TableHead>
                        <TableHead className="text-xs text-right">Écart</TableHead>
                        <TableHead className="text-xs text-center">Statut</TableHead>
                        <TableHead className="text-xs text-center">Annonces</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {competitorData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                            <Search className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                            <p className="font-medium">Aucune donnée de comparaison</p>
                            <p className="text-sm mt-1">Cliquez sur &quot;Analyser les prix&quot; pour lancer une analyse</p>
                          </TableCell>
                        </TableRow>
                      ) : competitorData.every(item => !item.market_avg || item.market_avg === 0) ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                            <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-amber-400" />
                            <p className="font-medium">AutoTrader bloque les requêtes automatisées</p>
                            <p className="text-sm mt-2 max-w-md mx-auto">
                              Pour obtenir des données de marché précises, vous pouvez:
                            </p>
                            <ul className="text-sm mt-2 text-left inline-block">
                              <li className="flex items-center gap-2 mt-1">
                                <Check className="h-4 w-4 text-green-500" />
                                Consulter AutoTrader.ca manuellement
                              </li>
                              <li className="flex items-center gap-2 mt-1">
                                <Check className="h-4 w-4 text-green-500" />
                                Utiliser Canadian Black Book ou CARFAX
                              </li>
                              <li className="flex items-center gap-2 mt-1">
                                <Check className="h-4 w-4 text-green-500" />
                                S&apos;abonner à l&apos;API officielle d&apos;AutoTrader
                              </li>
                            </ul>
                          </TableCell>
                        </TableRow>
                      ) : (
                        [...competitorData]
                          .sort((a, b) => {
                            const diffA = a.market_avg ? ((a.your_price - a.market_avg) / a.market_avg) : 0;
                            const diffB = b.market_avg ? ((b.your_price - b.market_avg) / b.market_avg) : 0;
                            return diffB - diffA;
                          })
                          .map((item, index) => {
                            const priceDiff = item.market_avg ? item.your_price - item.market_avg : 0;
                            const priceDiffPercent = item.market_avg ? ((priceDiff / item.market_avg) * 100) : 0;
                            
                            let statusBadge;
                            if (!item.market_avg || item.market_avg === 0) {
                              statusBadge = <Badge className="bg-slate-100 text-slate-600">Sans données</Badge>;
                            } else if (priceDiffPercent > 5) {
                              statusBadge = <Badge className="bg-red-100 text-red-700">Trop cher</Badge>;
                            } else if (priceDiffPercent < -5) {
                              statusBadge = <Badge className="bg-blue-100 text-blue-700">Sous-évalué</Badge>;
                            } else {
                              statusBadge = <Badge className="bg-green-100 text-green-700">Compétitif</Badge>;
                            }
                            
                            return (
                              <TableRow 
                                key={index} 
                                className={priceDiffPercent > 5 ? 'bg-red-50/50' : ''}
                              >
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div className="w-12 h-9 rounded bg-slate-100 overflow-hidden">
                                      {item.image ? (
                                        <img src={item.image} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <Car className="w-full h-full p-1.5 text-slate-300" />
                                      )}
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-slate-800">
                                        {item.year} {item.make} {item.model}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        {item.trim && <span className="mr-2">{item.trim}</span>}
                                        {item.mileage ? `${item.mileage.toLocaleString()} km` : ''}
                                      </p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right font-semibold text-slate-800">
                                  {formatPrice(item.your_price)}
                                </TableCell>
                                <TableCell className="text-right text-slate-600">
                                  {item.market_avg ? formatPrice(item.market_avg) : (
                                    <span className="text-slate-400 italic text-xs">Non disponible</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {item.market_avg ? (
                                    <span className={`font-medium ${priceDiff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                      {priceDiff > 0 ? '+' : ''}{formatPrice(priceDiff)}
                                      <span className="text-xs ml-1">
                                        ({priceDiffPercent > 0 ? '+' : ''}{priceDiffPercent.toFixed(1)}%)
                                      </span>
                                    </span>
                                  ) : '—'}
                                </TableCell>
                                <TableCell className="text-center">
                                  {statusBadge}
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-sm text-slate-600">
                                    {item.listings_found || 0}
                                  </span>
                                </TableCell>
                              </TableRow>
                            );
                          })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminPricing;
